import { useState } from 'react';

import { ordersBulkUpdate } from 'config/requests/ordersBulkUpdate';
import { useNotify } from 'react-admin';
import { UseFormReturn } from 'react-hook-form';
import { OrdersBulkUpdateError } from 'types/embedded/ordersBulkUpdate';

import { OrdersBulkUpdateValues } from '../OrdersBulkUpdateForm';

export type OrdersBulkUpdateResult = {
  foundIds: string[];
  notFoundIds: string[];
  notAllowedIds: string[];
};

type UseOrdersBulkUpdateReturn = {
  result: OrdersBulkUpdateResult;
  handleSubmit: (values: OrdersBulkUpdateValues) => void;
  clear: () => void;
};

const DEFAULT_RESULT: OrdersBulkUpdateResult = { foundIds: [], notFoundIds: [], notAllowedIds: [] };

const getResultMessage = ({
  foundIds,
  notFoundIds,
  notAllowedIds,
}: OrdersBulkUpdateResult): string => {
  if (notFoundIds.length === 0 && notAllowedIds.length === 0) {
    return 'Изменение статусов выполнено';
  }

  if (foundIds.length > 0) {
    return 'Изменение статусов выполнено частично';
  }

  return notAllowedIds.length > 0
    ? 'Изменение статуса невозможно, так как переданы заказы со статусами, для которых данное изменение запрещено.'
    : 'Изменение статусов не выполнено';
};

export const useOrdersBulkUpdate = (
  form: UseFormReturn<OrdersBulkUpdateValues>,
): UseOrdersBulkUpdateReturn => {
  const notify = useNotify();

  const [result, setResult] = useState<OrdersBulkUpdateResult>(DEFAULT_RESULT);

  const clear = (): void => {
    form.reset();
    setResult(DEFAULT_RESULT);
  };

  const handleSubmit = (values: OrdersBulkUpdateValues): void => {
    setResult(DEFAULT_RESULT);

    ordersBulkUpdate({
      order_ids: values.orderIds.replace(/\s/g, '').split(','),
      order_status: values.orderStatus,
    })
      .then((res) => {
        const result: OrdersBulkUpdateResult = {
          foundIds: res.data?.found_ids ?? [],
          notFoundIds: res.data?.not_found_ids ?? [],
          notAllowedIds: res.data?.not_allowed_ids ?? [],
        };

        setResult(result);

        notify(getResultMessage(result), { type: 'info' });
      })
      .catch((response) => {
        const errorList: OrdersBulkUpdateError[] = response?.data?.errors ?? [];

        if (response.status === 500) {
          notify(errorList[0]?.title ?? 'Внутренняя ошибка сервера', { type: 'error' });
          return;
        }

        errorList.forEach((error) =>
          form.setError(error.source.parameter, { message: error.title }),
        );

        notify('Ошибка в запросе!', { type: 'error' });
      });
  };

  return { result, handleSubmit, clear };
};
